import { useEffect, useMemo, useState, useRef } from 'react';
import styles from './chat.module.css';
import api, { baseURL } from '../../services/api';
import { toast } from 'react-toastify';
// chat styles are minimal; removed global import to avoid leaks

const normalizePhone = (s) => (s || '').replace(/\D/g, '');
const waLink = (numero, texto) => {
  const num = normalizePhone(numero);
  const msg = encodeURIComponent(texto || '');
  // Em desktop, o domínio web.whatsapp.com redireciona corretamente
  return `https://wa.me/${num}?text=${msg}`;
};
const getQrUrl = (path) => {
  if (!path || typeof path !== 'string' || path.trim() === '') return '';
  const file = path.split('/').pop();
  return `${baseURL || ''}/qrclientes/${file}`;
};

export default function Chat() {
  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState('proposta');
  const [customMsg, setCustomMsg] = useState('');
  const [hideHelp, setHideHelp] = useState(() => localStorage.getItem('wa_hide_help') === '1');
  const [autoPopout, setAutoPopout] = useState(() => localStorage.getItem('wa_auto_popout') === '1');
  const [selected, setSelected] = useState(null);
  const [qrcodes, setQrcodes] = useState([]);
  const [qrIndex, setQrIndex] = useState(0);
  const fileInputRef = useRef(null);
  const [upLoading, setUpLoading] = useState(false);
  const [upProgress, setUpProgress] = useState(0);
  const [chatAvailable, setChatAvailable] = useState(null); // null=desconhecido, false=desativado, true=ok
  const [popoutBlocked, setPopoutBlocked] = useState(false);
  const [editPhones, setEditPhones] = useState({}); // { [clienteId]: numero }
  const [sendText, setSendText] = useState('');
  const [sendType, setSendType] = useState('text'); // text|image|document
  const [sendMediaUrl, setSendMediaUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [venda, setVenda] = useState({
    data: new Date().toISOString().slice(0,10),
    compraBobDia: '',
    valorBRL: '',
    valorBOB: ''
  });
  // Mensagens de WhatsApp do cliente selecionado
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [pollMs, setPollMs] = useState(6000);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/clientes');
        setClientes(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        toast.error('Não foi possível carregar clientes');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Checa se o backend de WhatsApp/Chat está habilitado
  useEffect(() => {
    (async () => {
      try {
        // Se a rota existir, este request retorna 200 (mesmo sem mensagens)
        await api.get('/whatsapp/messages', { params: { clienteId: -1 } });
        setChatAvailable(true);
      } catch (e) {
        setChatAvailable(false);
      }
    })();
  }, []);

  // Tenta abrir/reutilizar a janela do WhatsApp Web ao entrar na tela, se habilitado
  useEffect(() => {
    if (!autoPopout) return;
    // Pequeno atraso para evitar bloquear renderização
    const t = setTimeout(() => {
      try { const ok = openWhatsPopout(); if (!ok) setPopoutBlocked(true); } catch { setPopoutBlocked(true); }
    }, 400);
    return () => clearTimeout(t);
  }, [autoPopout]);

  // Carrega QR Codes ao selecionar cliente
  useEffect(() => {
    if (!selected?.ID) { setQrcodes([]); setQrIndex(0); return; }
    (async () => {
      try {
        const resp = await api.get(`/clientes/${selected.ID}/qrcodes`);
        const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
        const urls = items.map((it) => getQrUrl(it.path));
        if (urls.length === 0 && selected.QRCODE_PATH) urls.push(getQrUrl(selected.QRCODE_PATH));
        setQrcodes(urls);
        setQrIndex(0);
      } catch {
        setQrcodes(selected?.QRCODE_PATH ? [getQrUrl(selected.QRCODE_PATH)] : []);
        setQrIndex(0);
      }
    })();
  }, [selected]);

  // Carrega e faz polling das mensagens de WhatsApp do cliente selecionado
  useEffect(() => {
    let timer;
    if (!selected?.ID) { setMessages([]); return; }
    const load = async () => {
      try {
        if (!selected?.ID) return;
        setLoadingMsgs(true);
        const r = await api.get(`/whatsapp/messages`, { params: { clienteId: selected.ID } });
        const items = Array.isArray(r.data?.items) ? r.data.items : [];
        // Ordena cronologicamente (antigo -> novo)
        items.sort((a, b) => new Date(a.CREATED_AT) - new Date(b.CREATED_AT));
        setMessages(items);
      } catch (e) {
        // Marca indisponibilidade do chat caso rota não exista
        setChatAvailable((prev) => prev === null ? false : prev);
      } finally {
        setLoadingMsgs(false);
      }
    };
    load();
    timer = setInterval(load, pollMs);
    return () => { if (timer) clearInterval(timer); };
  }, [selected, pollMs]);

  // Auto-scroll para o fim quando mensagens atualizam
  useEffect(() => {
    if (scrollRef.current) {
      try { scrollRef.current.scrollTop = scrollRef.current.scrollHeight; } catch {}
    }
  }, [messages]);

  const filtrados = useMemo(() => {
    const q = (busca || '').toUpperCase();
    return (clientes || []).filter((c) => (
      (c.NOME || '').toUpperCase().includes(q) ||
      (c.NOMEBRL || '').toUpperCase().includes(q) ||
      (c.CPF || '').includes(q) ||
      (c.WHATSAPP_NUMERO || '').includes(q)
    ));
  }, [clientes, busca]);

  const buildMessage = (c) => {
    if (template === 'custom' && customMsg.trim()) return customMsg.trim();
    switch (template) {
      case 'proposta':
        return `Olá ${c.NOME || c.NOMEBRL || ''}! Segue nossa proposta de câmbio. Qualquer dúvida fico à disposição.`;
      case 'pagamento':
        return `Olá ${c.NOME || c.NOMEBRL || ''}! Seguem as instruções de pagamento. Após concluir, nos avise aqui.`;
      case 'recibo':
        return `Olá ${c.NOME || c.NOMEBRL || ''}! Segue o recibo/comprovante do câmbio realizado. Obrigado!`;
      default:
        return '';
    }
  };

  const salvarWhatsapp = async (c, numero) => {
    try {
      const n = normalizePhone(numero);
      await api.put(`/clientes/${c.ID}`, { whatsappNumero: n });
      toast.success('WhatsApp salvo');
      setClientes((prev) => prev.map(x => x.ID === c.ID ? { ...x, WHATSAPP_NUMERO: n } : x));
      setEditPhones((prev) => ({ ...prev, [c.ID]: n }));
    } catch (e) {
      toast.error('Falha ao salvar WhatsApp');
    }
  };

  const openWhatsPopout = () => {
    const features = 'popup=yes,width=1100,height=800,noopener,noreferrer';
    const w = window.open('https://web.whatsapp.com', 'wa_web', features);
    return !!w;
  };

  const openConversation = (c) => {
    const numero = c.WHATSAPP_NUMERO || '';
    if (!numero) { toast.warn('Cadastre o WhatsApp do cliente'); return; }
    const msg = buildMessage(c);
    registrarContato(c, msg);
    // Garante a janela e navega nela
    openWhatsPopout();
    const link = waLink(numero, msg);
    window.open(link, 'wa_web');
  };

  const registrarContato = async (c, msg) => {
    try {
      await api.post('/chat/logs', {
        clienteId: c.ID,
        telefone: c.WHATSAPP_NUMERO || null,
        tipo: template,
        resumo: (msg || '').slice(0, 250)
      });
    } catch {
      // marca possível desativação (rota ausente)
      setChatAvailable((prev) => prev === null ? false : prev);
    }
  };

  const renderMessage = (m) => {
    const isOut = (m.DIRECTION || '').toUpperCase() === 'OUT';
    const isIn = !isOut;
    const time = m.CREATED_AT ? new Date(m.CREATED_AT) : null;
    const hour = time ? time.toLocaleString() : '';
    const hasImage = (m.TYPE || '').toLowerCase() === 'image' && (m.MEDIA_FILENAME || m.MEDIA_URL);
    const hasDoc = (m.TYPE || '').toLowerCase() === 'document' && (m.MEDIA_FILENAME || m.MEDIA_URL);
    const mediaUrl = m.MEDIA_URL
      ? m.MEDIA_URL
      : (m.MEDIA_FILENAME ? `${baseURL || ''}/qrclientes/${m.MEDIA_FILENAME}` : null);
    return (
      <div key={m.MSG_ID}
        style={{
          display: 'flex',
          justifyContent: isOut ? 'flex-end' : 'flex-start',
          margin: '6px 0'
        }}
      >
        <div style={{
          maxWidth: '75%',
          background: isOut ? '#dff7c6' : '#fff',
          border: '1px solid #e3e3e3',
          borderRadius: 8,
          padding: '8px 10px',
          boxShadow: '0 1px 1px rgba(0,0,0,0.03)'
        }}>
          {hasImage && (
            <div style={{ marginBottom: 6 }}>
              <img src={mediaUrl} alt="imagem" style={{ maxWidth: '100%', borderRadius: 6 }} />
            </div>
          )}
          {hasDoc && (
            <div style={{ marginBottom: 6 }}>
              <a href={mediaUrl} target="_blank" rel="noreferrer">Abrir documento</a>
            </div>
          )}
          {m.TEXT && <div style={{ whiteSpace: 'pre-wrap' }}>{m.TEXT}</div>}
          <div style={{ textAlign: 'right', color: '#666', fontSize: 11, marginTop: 4 }}>{hour}</div>
        </div>
      </div>
    );
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (!selected?.ID) { toast.warn('Selecione um cliente'); return; }
    const files = e.dataTransfer?.files || [];
    if (!files.length) return;
    await uploadQr(files[0]);
  };

  const handlePaste = async (e) => {
    if (!selected?.ID) return;
    const items = e.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === 'file') {
        const file = it.getAsFile();
        if (file && /^image\//.test(file.type)) {
          await uploadQr(file);
          break;
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [selected]);

  const uploadQr = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) { toast.error('Apenas JPEG/PNG'); return; }
    if (file.size > 5*1024*1024) { toast.error('Máximo 5MB'); return; }
    const fd = new FormData();
    fd.append('qrcode', file);
    try {
      setUpLoading(true); setUpProgress(0);
      const resp = await api.post(`/clientes/${selected.ID}/upload-qrcode`, fd, {
        onUploadProgress: (ev) => {
          if (!ev.total) return; setUpProgress(Math.round((ev.loaded*100)/ev.total));
        }
      });
      const url = getQrUrl(`${resp.data.qrcodePath}?t=${Date.now()}`);
      setQrcodes((prev) => [url, ...prev]); setQrIndex(0);
      toast.success('Imagem adicionada ao cliente');
    } catch (e) {
      toast.error('Falha ao enviar imagem');
    } finally {
      setUpLoading(false);
    }
  };

  const calcVenda = () => {
    const compra = parseFloat(venda.compraBobDia) || 0;
    const vBRL = parseFloat(venda.valorBRL) || 0;
    const vBOB = parseFloat(venda.valorBOB) || 0;
    const taxaDia = vBRL !== 0 ? vBOB / vBRL : 0;
    const lucroBOB = vBRL * compra - vBOB;
    const lucroBRL = compra !== 0 ? lucroBOB / compra : 0;
    const percBRL = vBRL !== 0 ? (lucroBRL / vBRL) * 100 : 0;
    return { taxaDia, lucroBOB, lucroBRL, percBRL };
  };

  const criarVenda = async () => {
    if (!selected?.ID) { toast.warn('Selecione um cliente'); return; }
    if (!venda.data || !venda.compraBobDia || !venda.valorBRL) { toast.warn('Preencha data, compra e valor BRL'); return; }
    const { taxaDia, lucroBOB, lucroBRL, percBRL } = calcVenda();
    try {
      await api.post('/vendas', {
        idCliente: selected.ID,
        data: venda.data,
        compraBobDia: parseFloat(venda.compraBobDia) || 0,
        nomeBRL: selected.NOME || selected.NOMEBRL || '',
        nomeBOB: selected.NOMEBOB || '',
        valorBRL: parseFloat(venda.valorBRL) || 0,
        valorBOB: parseFloat(venda.valorBOB) || 0,
        taxaDia: Number(taxaDia.toFixed(2)),
        lucroBOB: Number(lucroBOB.toFixed(2)),
        lucroBRL: Number(lucroBRL.toFixed(2)),
        percBRL: Number(percBRL.toFixed(2))
      });
      toast.success('Venda lançada com sucesso');
      // reset minimal fields
      setVenda((p) => ({ ...p, valorBRL: '', valorBOB: '' }));
    } catch (e) {
      toast.error('Falha ao lançar venda');
    }
  };

  const sendViaCloudApi = async () => {
    if (!selected?.ID) { toast.warn('Selecione um cliente'); return; }
    const numero = editPhones[selected.ID] ?? selected.WHATSAPP_NUMERO ?? '';
    if (!numero) { toast.warn('Cadastre o WhatsApp do cliente'); return; }
    if (sendType === 'text' && !sendText.trim()) { toast.warn('Digite uma mensagem'); return; }
    if ((sendType === 'image' || sendType === 'document') && !sendMediaUrl.trim()) { toast.warn('Informe a URL da mídia'); return; }
    try {
      setSending(true);
      await api.post('/whatsapp/send', {
        to: numero,
        text: sendText.trim(),
        type: sendType,
        mediaUrl: (sendType === 'image' || sendType === 'document') ? sendMediaUrl.trim() : undefined,
      });
      // Otimismo: adiciona no feed local
      setMessages((prev) => ([
        ...prev,
        {
          MSG_ID: `local-${Date.now()}`,
          DIRECTION: 'OUT',
          TYPE: sendType,
          TEXT: sendText.trim(),
          MEDIA_URL: (sendType === 'image' || sendType === 'document') ? sendMediaUrl.trim() : undefined,
          CREATED_AT: new Date().toISOString()
        }
      ]));
      setSendText('');
      setSendMediaUrl('');
    } catch (e) {
      setChatAvailable((prev) => prev === null ? false : prev);
      toast.error('Falha ao enviar via API do WhatsApp');
    } finally {
      setSending(false);
    }
  };

  return (
    <main className={styles.chat}>
      {/* Coluna esquerda: clientes e atalhos */}
      <div style={{ flex: 1, padding: 16, overflow: 'auto', borderRight: '1px solid #e5e5e5' }}>
        <h2>Chat (WhatsApp)</h2>
        {chatAvailable === false && (
          <div style={{ background: '#fff4e5', border: '1px solid #ffd8a8', color: '#663c00', padding: 8, borderRadius: 6, marginBottom: 8 }}>
            Chat/WhatsApp desativado no servidor. Peça para habilitar CHAT_ENABLED=true e configurar tokens.
          </div>
        )}
        {/* Ferramentas Admin */}
        <div style={{ marginBottom: 8 }}>
          <button onClick={() => setShowAdmin(v => !v)}>{showAdmin ? 'Ocultar' : 'Mostrar'} ferramentas admin</button>
          {showAdmin && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={async () => { try { await api.post('/admin/migrate-chat'); toast.success('Migração CHAT ok'); } catch { toast.error('Falha ao migrar CHAT'); } }}>Migrar CHAT</button>
              <button onClick={async () => { try { await api.post('/admin/migrate-whatsapp'); toast.success('Migração WHATSAPP ok'); } catch { toast.error('Falha ao migrar WHATSAPP'); } }}>Migrar WHATSAPP</button>
              <button onClick={async () => { try { await api.post('/admin/seed-whatsapp-demo'); toast.success('Seed de teste ok'); } catch { toast.error('Falha ao rodar seed'); } }}>Seed mensagens demo</button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={openWhatsPopout}>Abrir WhatsApp Web (pop‑out)</button>
          <input
            type="text"
            placeholder="Buscar cliente/NOME/CPF/WhatsApp"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
        <select value={template} onChange={(e) => setTemplate(e.target.value)}>
          <option value="proposta">Template: Proposta</option>
          <option value="pagamento">Template: Instruções de pagamento</option>
          <option value="recibo">Template: Recibo</option>
          <option value="custom">Template: Personalizado</option>
        </select>
        {template === 'custom' && (
          <input
            type="text"
            placeholder="Mensagem personalizada"
            value={customMsg}
            onChange={(e) => setCustomMsg(e.target.value)}
            style={{ flex: 2, minWidth: 260 }}
          />
        )}
      </div>

      {loading ? (
        <p>Carregando clientes…</p>
      ) : (
        <div style={{ marginTop: 12 }}>
          {filtrados.slice(0, 200).map((c) => {
            const msg = buildMessage(c);
            const numero = c.WHATSAPP_NUMERO || '';
            const link = numero ? waLink(numero, msg) : null;
            return (
              <div
                key={c.ID}
                onClick={() => setSelected(c)}
                style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee', flexWrap: 'wrap', cursor: 'pointer', background: selected?.ID === c.ID ? '#f7faff' : 'transparent' }}
              >
                <div style={{ minWidth: 200, fontWeight: 500 }}>{c.NOME || c.NOMEBRL || 'Sem nome'}</div>
                <input
                  type="tel"
                  placeholder="WhatsApp (somente números)"
                  value={editPhones[c.ID] ?? numero}
                  onChange={(e) => setEditPhones((p) => ({ ...p, [c.ID]: e.target.value.replace(/\D/g, '') }))}
                  onBlur={() => salvarWhatsapp(c, editPhones[c.ID] ?? numero)}
                  onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); salvarWhatsapp(c, editPhones[c.ID] ?? numero); } }}
                  style={{ width: 180 }}
                />
                <button onClick={() => salvarWhatsapp(c, editPhones[c.ID] ?? numero)}>Salvar</button>
                <button
                  disabled={!link}
                  onClick={(ev) => { ev.stopPropagation(); registrarContato(c, msg); openConversation(c); }}
                >
                  Abrir conversa
                </button>
                <button onClick={async () => {
                  try {
                    await navigator.clipboard?.writeText(msg);
                    toast.info('Mensagem copiada');
                  } catch {
                    toast.error('Não foi possível copiar para a área de transferência');
                  }
                }}>Copiar mensagem</button>
                <a href={link || '#'} onClick={(ev) => { ev.stopPropagation(); registrarContato(c, msg); }} target="wa_web" rel="noreferrer">wa.me</a>
              </div>
            );
          })}
        </div>
      )}
      </div>
      {/* Coluna direita: painel de instrução/placeholder do WhatsApp Web */}
      <div
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={handleDrop}
        style={{ width: 520, maxWidth: '50vw', padding: 16 }}
      >
        <h3>WhatsApp Web</h3>
        {!hideHelp && (
          <>
            <p style={{ color: '#666', marginTop: 8 }}>
              Por políticas de segurança do WhatsApp, não é possível embutir o WhatsApp Web dentro do app.
              Use o botão abaixo para abrir uma janela dedicada e reutilizável (pop‑out) ao lado desta tela.
            </p>
            {popoutBlocked && (
              <div style={{ background: '#ffe3e3', border: '1px solid #ffa8a8', color: '#7a0000', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                O navegador bloqueou a janela pop‑out. Clique em "Abrir/Trazer WhatsApp Web" e permita pop‑ups para este site.
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={autoPopout}
                  onChange={(e) => {
                    const v = e.target.checked; setAutoPopout(v);
                    try { localStorage.setItem('wa_auto_popout', v ? '1' : '0'); } catch {}
                  }}
                />
                Abrir automaticamente ao entrar no Chat
              </label>
            </div>
            <div style={{ marginTop: 6 }}>
              <button onClick={() => { setHideHelp(true); try { localStorage.setItem('wa_hide_help', '1'); } catch {} }}>Não mostrar novamente</button>
            </div>
          </>
        )}
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={openWhatsPopout}>Abrir/Trazer WhatsApp Web</button>
          <a href="https://web.whatsapp.com" target="wa_web" rel="noreferrer">Abrir na janela nomeada</a>
          <a href="whatsapp://" rel="noreferrer">Tentar abrir app WhatsApp</a>
        </div>

        {/* Conversa do cliente selecionado */}
        {selected && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Conversa</div>
            <div ref={scrollRef} style={{
              border: '1px solid #eee', borderRadius: 8, padding: 10, height: 260,
              overflow: 'auto', background: '#f9fafb'
            }}>
              {loadingMsgs && messages.length === 0 ? (
                <div style={{ color: '#777' }}>Carregando mensagens…</div>
              ) : (
                messages.length === 0 ? (
                  <div style={{ color: '#777' }}>Sem mensagens recentes.</div>
                ) : (
                  messages.map(renderMessage)
                )
              )}
            </div>
            {/* Enviar via Cloud API (texto/imagem/documento) */}
            <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '110px 1fr 1fr auto', gap: 6, alignItems: 'center' }}>
              <select value={sendType} onChange={(e) => setSendType(e.target.value)}>
                <option value="text">Texto</option>
                <option value="image">Imagem (URL)</option>
                <option value="document">Documento (URL)</option>
              </select>
              <input
                type="text"
                placeholder="Mensagem (caption opcional)"
                value={sendText}
                onChange={(e) => setSendText(e.target.value)}
              />
              {(sendType === 'image' || sendType === 'document') && (
                <input
                  type="url"
                  placeholder="URL da mídia"
                  value={sendMediaUrl}
                  onChange={(e) => setSendMediaUrl(e.target.value)}
                />
              )}
              <button disabled={sending || chatAvailable === false} onClick={sendViaCloudApi}>
                {sending ? 'Enviando…' : 'Enviar' }
              </button>
            </div>
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#666', fontSize: 12 }}>Atualiza a cada</span>
              <select value={pollMs} onChange={(e) => setPollMs(Number(e.target.value))}>
                <option value={4000}>4s</option>
                <option value={6000}>6s</option>
                <option value={10000}>10s</option>
                <option value={15000}>15s</option>
              </select>
            </div>
          </div>
        )}

        {/* Painel do cliente selecionado */}
        {selected && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 600 }}>{selected.NOME || selected.NOMEBRL}</div>
            {/* Carrossel simples */}
            <div style={{ marginTop: 8, textAlign: 'center' }}>
              {(qrcodes.length > 0 || getQrUrl(selected.QRCODE_PATH)) ? (
                <img src={qrcodes.length > 0 ? qrcodes[qrIndex] : getQrUrl(selected.QRCODE_PATH)} alt="QR" style={{ maxWidth: '100%', borderRadius: 8 }} />
              ) : (
                <div style={{ color: '#777' }}>Sem imagens/QR vinculados.</div>
              )}
              {qrcodes.length > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 6 }}>
                  <button onClick={() => setQrIndex((p) => (p - 1 + qrcodes.length) % qrcodes.length)}>◀</button>
                  <span>{qrIndex + 1}/{qrcodes.length}</span>
                  <button onClick={() => setQrIndex((p) => (p + 1) % qrcodes.length)}>▶</button>
                </div>
              )}
            </div>

            {/* Upload zona: arquivo, arrastar/colar */}
            <div style={{ marginTop: 10 }}>
              <div style={{ border: '2px dashed #bbb', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                <p style={{ margin: 0 }}>Arraste/cole aqui uma imagem para vincular ao cliente</p>
                <div style={{ marginTop: 8 }}>
                  <input type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} ref={fileInputRef} onChange={(e) => uploadQr(e.target.files?.[0])} />
                  <button onClick={() => fileInputRef.current?.click?.()} disabled={upLoading}>{upLoading ? `Enviando ${upProgress}%` : 'Escolher imagem'}</button>
                </div>
              </div>
            </div>

            {/* Lançar venda rápida */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Lançar venda</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <input type="date" value={venda.data} onChange={(e) => setVenda({ ...venda, data: e.target.value })} />
                <input type="text" inputMode="decimal" placeholder="Compra (taxa)" value={venda.compraBobDia} onChange={(e) => setVenda({ ...venda, compraBobDia: e.target.value })} />
                <input type="text" inputMode="decimal" placeholder="Valor BRL" value={venda.valorBRL} onChange={(e) => setVenda({ ...venda, valorBRL: e.target.value })} />
                <input type="text" inputMode="decimal" placeholder="Valor BOB" value={venda.valorBOB} onChange={(e) => setVenda({ ...venda, valorBOB: e.target.value })} />
              </div>
              <div style={{ marginTop: 6, color: '#555' }}>
                {(() => { const { taxaDia, lucroBOB, lucroBRL, percBRL } = calcVenda(); return (
                  <span>Tx: {taxaDia.toFixed(2)} | LBOB: {lucroBOB.toFixed(2)} | LBRL: {lucroBRL.toFixed(2)} | %BRL: {percBRL.toFixed(2)}</span>
                ); })()}
              </div>
              <div style={{ marginTop: 8 }}>
                <button onClick={criarVenda}>Lançar venda</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
